<template>
  <div class="content-box">
    <h5 class="title is-5">{{ $t("projects.base") }}</h5>
    <AlertMessage color="is-info" :message="editMessage"></AlertMessage>
    <Form
      @submit="doEditProject"
      :validation-schema="schema"
      :initial-values="formValues"
      v-slot="{ errors }"
    >
      <Field name="tenantId" type="hidden" />
      <div class="field">
        <label class="label">{{ $t("projects.projectId") }}</label>
        <Field
          name="projectId"
          id="projectId"
          class="input"
          type="text"
          maxlength="60"
          disabled="disabled"
        />
      </div>

      <div class="field">
        <label class="label">{{ $t("projects.projectName") }} *</label>
        <div class="control">
          <Field
            name="projectName"
            class="input"
            type="text"
            placeholder="ex) ○○工場向けプロジェクト"
            maxlength="60"
            required="required"
            :class="{ 'is-danger': errors.projectName }"
          />
          <span class="help is-danger">{{ errors.projectName }}</span>
        </div>
      </div>

      <div class="field">
        <label class="label">{{ $t("projects.projectOwner") }} *</label>
        <div class="control">
          <Field
            name="projectOwner"
            class="input"
            type="text"
            placeholder="email"
            maxlength="60"
            required="required"
            :class="{ 'is-danger': errors.projectOwner }"
          />
          <span class="help is-danger">{{ errors.projectOwner }}</span>
        </div>
      </div>

      <div class="field">
        <label class="label">{{ $t("Timezone") }}</label>
        <div class="select">
          <Field name="zoneinfo" as="select" class="form-control select-lang">
            <option value="9">Asia/Tokyo(JST)</option>
            <option value="-5">America/New_York(EST)</option>
          </Field>
        </div>
      </div>

      <div v-if="isSecondaryUse && plan == 'standard'">
        <div class="field">
          <label class="label">{{ $t("projects.secondaryUseTitle") }}</label>
          <label class="checkbox">
            <div class="control">
              <Field
                name="secondaryUse"
                type="checkbox"
                unchecked-value="false"
                value="true"
              />
              {{ $t("projects.secondaryUseMessage") }}
            </div>
          </label>
        </div>
        <div class="field">
          <a href="#" @click="showModal">{{
            $t("projects.secondaryUseLink")
          }}</a>
        </div>
      </div>

      <div v-if="plan == 'agency'">
        <Field
          name="secondaryUse"
          type="hidden"
          unchecked-value="false"
          value="true"
        />
      </div>

      <button
        type="submit"
        class="button is-block is-primary mt-4"
        v-bind:class="{ 'is-loading': isLoad }"
      >
        {{ $t("Edit") }}
      </button>
    </Form>
  </div>
  <Secondary
    :imageModalView="modalView"
    :company="company"
    @close="closeModal"
  />
</template>

<script>
import AlertMessage from "@/components/AlertMessage.vue";
import Secondary from "@/components/projects/Secondary.vue";
import imsr from "@/imsr-api";
import { ImsrProjects } from "@/lib/imsrProjects.js";
import { ref } from "@vue/reactivity";
import { Field, Form } from "vee-validate";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import * as Yup from "yup";

export default {
  props: {
    projectId: {
      type: String,
      require: true,
    },
    tenantId: {
      type: String,
      require: true,
    },
    isSecondaryUse: {
      type: Boolean,
      require: true,
    },
    company: {
      type: String,
      require: true,
    },
    plan: {
      type: String,
      default: "standard",
    },
  },
  components: {
    AlertMessage,
    Secondary,
    Form,
    Field,
  },
  setup(props) {
    const { t } = useI18n();
    const store = useStore();
    const router = useRouter();
    const isLoad = ref(false);
    const formValues = ref({});
    const editMessage = ref();
    const modalView = ref(false);

    const schema = Yup.object().shape({
      projectName: Yup.string().required(t("validate.required")),
      projectOwner: Yup.string()
        .email(t("validate.email"))
        .required(t("validate.required")),
      secondaryUse: Yup.string(),
      zoneinfo: Yup.string().required(t("validate.required")),
    });

    const getProject = async () => {
      try {
        const imsrProjects = new ImsrProjects();
        formValues.value = await imsrProjects.getProject(
          props.tenantId,
          props.projectId
        );
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };
    getProject();

    const doEditProject = async (values) => {
      try {
        isLoad.value = true;
        const params = {
          tenantId: values.tenantId,
          projectId: values.projectId,
          projectName: values.projectName,
          projectOwner: values.projectOwner,
          secondaryUse: values.secondaryUse,
          zoneinfo: values.zoneinfo.toString(),
        };
        await imsr.imsrPutApi("/projects", params);
        editMessage.value = t("projects.editSuccess");
        store.commit("setProjectName", params.projectName);
      } catch (e) {
        if (e.status == "400") {
          editMessage.value = t("validate.invalid");
        } else {
          router.push({ name: "InternalServerError" });
        }
      }
      isLoad.value = false;
    };

    const showModal = () => {
      modalView.value = true;
    };

    const closeModal = () => {
      modalView.value = false;
    };

    return {
      isLoad,
      doEditProject,
      schema,
      formValues,
      editMessage,
      modalView,
      showModal,
      closeModal,
    };
  },
};
</script>
